import axios, { AxiosError } from 'axios'
import i18n from '../i18n'
import type {
  ApiErrorDetailDict,
  ApiErrorPayload,
  ExtractedApiError,
} from './apiTypes'

// T011 (2026-06-07): The backend's HTTPException bridge stringifies dict-shape
// detail payloads via Python's ``str(dict)`` and surfaces the literal in the
// top-level ``message`` field — e.g. ``{'code': 'pack_authoring.permission_denied',
// 'message': '...'}``. That Python repr leaks into error alerts on /app/ops and
// the Ready Made Pack authoring dialog. We detect and reject the leak shape
// here so we never propagate it to the UI.
const PYTHON_DICT_LITERAL_PATTERN = /^\{\s*['"]/
const isPythonDictLiteral = (value: unknown): boolean => {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (!PYTHON_DICT_LITERAL_PATTERN.test(trimmed)) return false
  // Python repr uses single quotes; JSON strings use double quotes.
  return trimmed.includes("'")
}

const asDetailDict = (
  detail: ApiErrorPayload['detail']
): ApiErrorDetailDict | null => {
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    return detail as ApiErrorDetailDict
  }
  return null
}

// Pick the cleanest human-facing message available across all the legacy
// envelope shapes:
//   1. ``detail`` is a dict with ``message`` → use ``detail.message`` (e.g. the
//      ready-made-pack permission_denied path).
//   2. ``payload.message`` is a real string (not the Python-dict leak) → use it.
//   3. ``detail`` is a string → use it.
//   4. Otherwise, fall through to the caller's fallback.
const pickHumanMessage = (
  payload: ApiErrorPayload
): string | undefined => {
  const detailDict = asDetailDict(payload.detail)
  if (detailDict && typeof detailDict.message === 'string' && detailDict.message) {
    return detailDict.message
  }
  if (
    typeof payload.message === 'string' &&
    payload.message.length > 0 &&
    !isPythonDictLiteral(payload.message)
  ) {
    return payload.message
  }
  if (typeof payload.detail === 'string' && payload.detail.length > 0) {
    return payload.detail
  }
  return undefined
}

const pickErrorCode = (payload: ApiErrorPayload): string | undefined => {
  if (typeof payload.code === 'string' && payload.code.length > 0) {
    return payload.code
  }
  const detailDict = asDetailDict(payload.detail)
  if (detailDict && typeof detailDict.code === 'string' && detailDict.code) {
    return detailDict.code
  }
  return undefined
}

// T011 (2026-06-07): Compose just the human message + optional hint.
// Correlation ID is intentionally NOT concatenated here — it stays on the
// ``ApiRequestError`` object as a property so display components can surface
// it as a secondary line (smaller text). Concatenating it inline produced the
// QA-complaint "Correlation ID: c98f..." trailer on every alert.
//
// 2026-07-08: that left a dead end for real 500s — the backend's
// catch_all_handler always sets a hint telling the user to "include the
// correlation ID if this keeps happening" (see error_handlers.py
// _default_hint, status>=500 only), but no display component anywhere in
// the app actually renders ``error.correlationId``, so the instruction was
// unfollowable. Only append it for genuine system-level failures (5xx) —
// this deliberately does NOT fire for validation/permission/4xx errors,
// preserving the original QA fix.
const isSystemLevelError = (error: ExtractedApiError): boolean =>
  (error.status !== undefined && error.status >= 500) ||
  error.category === 'system'

const composeApiErrorMessage = (error: ExtractedApiError): string => {
  const lines = [error.message]

  if (error.hint) {
    lines.push(`Hint: ${error.hint}`)
  }

  if (error.correlationId && isSystemLevelError(error)) {
    lines.push(`Correlation ID: ${error.correlationId}`)
  }

  return lines.filter(Boolean).join('\n')
}

export class ApiRequestError extends Error {
  status?: number
  code?: string
  category?: string
  severity?: string
  hint?: string
  correlationId?: string
  payload?: ApiErrorPayload
  response?: AxiosError['response']
  userMessage: string

  constructor(extracted: ExtractedApiError) {
    super(composeApiErrorMessage(extracted))
    this.name = 'ApiRequestError'
    this.status = extracted.status
    this.code = extracted.code
    this.category = extracted.category
    this.severity = extracted.severity
    this.hint = extracted.hint
    this.correlationId = extracted.correlationId
    this.payload = extracted.payload
    this.userMessage = this.message
  }
}

export const extractApiError = (error: unknown): ExtractedApiError => {
  if (error instanceof ApiRequestError) {
    const cleanMessage = error.payload
      ? pickHumanMessage(error.payload)
      : undefined
    return {
      status: error.status,
      message: cleanMessage || error.userMessage || error.message,
      detail: error.payload?.detail,
      hint: error.hint,
      correlationId: error.correlationId,
      code: error.code,
      category: error.category,
      severity: error.severity,
      payload: error.payload,
    }
  }

  if (axios.isAxiosError(error)) {
    const payload = (error.response?.data || {}) as ApiErrorPayload
    const correlationHeader = error.response?.headers?.['x-correlation-id']
    // T011: legacy fallback — if every field is missing/malformed, still
    // surface SOMETHING (axios message → generic Korean string). Never expose
    // Python-dict literal form.
    const cleanMessage =
      pickHumanMessage(payload) ||
      (error.message && !isPythonDictLiteral(error.message)
        ? error.message
        : undefined) ||
      '요청 처리 중 오류가 발생했습니다.'
    return {
      status: error.response?.status,
      message: cleanMessage,
      detail: payload.detail,
      hint: payload.hint,
      correlationId:
        payload.correlation_id ||
        (Array.isArray(correlationHeader)
          ? correlationHeader[0]
          : correlationHeader),
      code: pickErrorCode(payload),
      category: payload.category,
      severity: payload.severity,
      payload,
    }
  }

  if (error instanceof Error) {
    return {
      message: error.message,
    }
  }

  return {
    message: i18n.t('serviceErrors.unknownError'),
  }
}

export const getApiErrorMessage = (
  error: unknown,
  fallback?: string
): string => {
  const extracted = extractApiError(error)
  const message = composeApiErrorMessage(extracted)
  return message || fallback || '요청 처리 중 오류가 발생했습니다.'
}

const toApiRequestError = (error: unknown): ApiRequestError => {
  if (error instanceof ApiRequestError) {
    return error
  }

  const extracted = extractApiError(error)
  const apiError = new ApiRequestError(extracted)

  if (axios.isAxiosError(error)) {
    apiError.response = error.response
  }

  return apiError
}

const AUTH_EXPIRED_EVENT = 'infergraph-auth-expired'

const clearExpiredAuthSession = () => {
  try {
    if (typeof localStorage === 'undefined') return
    if (!localStorage.getItem('auth_token')) return

    localStorage.removeItem('userProfile')
    localStorage.removeItem('isDemoUser')
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_user')
    localStorage.removeItem('refresh_token')

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT))
    }
  } catch (e) {
    console.warn('만료된 인증 세션 정리 실패:', e)
  }
}

const clearExpiredAuthSessionOn401 = (error: unknown) => {
  if (!axios.isAxiosError(error)) return
  if (error.response?.status !== 401) return
  clearExpiredAuthSession()
}

// 기본값은 same-origin 상대경로를 사용한다.
// 개발 서버에서는 Vite proxy(`/api` -> backend)가 처리하고, 배포 환경에서는 nginx가 처리한다.
// 절대 localhost URL을 기본값으로 쓰면 127.0.0.1 프론트엔드에서 CORS가 발생한다.
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 응답 인터셉터 - 에러 처리
api.interceptors.response.use(
  (response) => response,
  (error) => {
    clearExpiredAuthSessionOn401(error)
    return Promise.reject(toApiRequestError(error))
  }
)

// Canonical auth-header derivation. Exported so raw fetch callers (e.g. the
// compiler SSE stream, which can't use the axios interceptor) send the exact
// same headers the interceptor adds — keeping a single source of truth.
export function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}
  try {
    if (typeof localStorage === 'undefined') return headers
    const token = localStorage.getItem('auth_token')
    if (token) {
      headers.Authorization = `Bearer ${token}`
      return headers
    }
    const rawProfile = localStorage.getItem('userProfile')
    if (rawProfile) {
      const profile = JSON.parse(rawProfile)
      if (profile?.email) {
        headers['X-User-Email'] = profile.email
        headers['X-Global-Admin'] = profile.isGlobalAdmin ? 'true' : 'false'
      }
    }
  } catch (e) {
    console.warn('사용자 프로필 헤더 추가 실패:', e)
  }
  return headers
}

// 요청 인터셉터 - 사용자 메타데이터 헤더 추가 (전역 관리자 등)
api.interceptors.request.use((config) => {
  const extra = getAuthHeaders()
  if (Object.keys(extra).length > 0) {
    config.headers = {
      ...(config.headers || {}),
      ...extra,
    } as unknown as typeof config.headers
  }
  return config
})
